/**
 * Хук для работы с чеклистом диагностики.
 * 
 * Загружает пункты чеклиста и результаты по заявке.
 * Отвечает за сохранение результатов и валидацию перед завершением.
 * 
 * Применяется: Diagnostic
 */

import { useState, useEffect } from 'react'

export function useChecklist(orderId, token, onLoaded) {
    const [items, setItems] = useState([])
    const [results, setResults] = useState({})
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        Promise.all([
            fetch(`/api/diagnostic/items?orderId=${orderId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
            fetch(`/api/diagnostic/results/${orderId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
            fetch(`/api/orders/${orderId}/intake`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]).then(([itemsData, resultsData, intakeData]) => {
            setItems(itemsData)
            const resultsMap = {}
            resultsData.forEach(r => {
                const key = `${r.item_id}_${r.side}`
                resultsMap[key] = r
            })
            setResults(resultsMap)
            setLoading(false)
            if (onLoaded) onLoaded(resultsData, intakeData, itemsData)
        })
    }, [orderId, token])

    const saveResult = async (orderId, itemId, side, status) => {
        try {
            const res = await fetch('/api/diagnostic/results', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ order_id: orderId, item_id: itemId, side: side || 'none', status, comment: '' })
            })
            const data = await res.json()
            const key = `${itemId}_${side || 'none'}`
            setResults(prev => ({ ...prev, [key]: data }))
            return data
        } catch (err) {
            console.error(err)
        }
    }

    const saveMileage = async (orderId, value) => {
        const res = await fetch(`/api/orders/${orderId}/mileage`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ mileage: value === '' ? null : Number(value) })
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.message || 'Не удалось сохранить пробег')
        return data
    }

    const validateChecklist = (mileageInput, photos) => {
        const missing = []
        if (!mileageInput) missing.push('mileage')
        if (!photos['15_none']?.length) missing.push('mileage_photo')
        const checklistItems = items.filter(i => i.group_name !== 'Приёмка' && i.group_name !== 'Дополнительный осмотр')
        checklistItems.forEach(item => {
            const sides = item.has_sides ? ['left', 'right'] : ['none']
            sides.forEach(side => {
                const key = `${item.id}_${side}`
                if (!results[key]) missing.push(key)
            })
        })
        Object.entries(results).forEach(([key, result]) => {
            if (key === '15_none') return
            const needsPhoto = result.status === 'warning' || result.status === 'critical'
            if (needsPhoto && !(photos[key]?.length > 0)) missing.push(key)
        })
        return missing
    }

    const getStatus = (itemId, side) => {
        const key = `${itemId}_${side || 'none'}`
        return results[key]?.status
    }

    return { items, results, setResults, loading, saveResult, saveMileage, validateChecklist, getStatus }
}
